import { createTRPCProxyClient, httpBatchLink, splitLink } from "@trpc/client";
import type { AppRouter } from "../../server/src/api/root";
import { fetchAuthSession } from "aws-amplify/auth";

const localUrl = process.env.REACT_APP_LOCAL_URL as string;
const cloudfrontUrl = process.env.REACT_APP_CLOUDFRONT_URL as string;
/**
 * tRPCサーバーのベースURLを返す
 * - localhost:3000 からのアクセスはローカルURL
 * - それ以外はCloudFront URL
 * - window未定義（テスト環境）の場合はローカルURL
 * @returns サーバーのベースURL文字列
 */
export const getBaseUrl = () => {
  // Ensure we have default URLs for test environment
  const defaultLocalUrl = localUrl || "http://localhost:3000";
  const defaultCloudfrontUrl = cloudfrontUrl || "https://example.cloudfront.net";

  // Check if we're in a test environment (no window object)
  if (typeof window === "undefined") {
    return defaultLocalUrl;
  }

  const currentUrl = window.location.href;
  if (currentUrl.startsWith("http://localhost:3000")) {
    return defaultLocalUrl;
  } else {
    return defaultCloudfrontUrl;
  }
};

/**
 * CognitoのアクセストークンをAmplify経由で取得する
 * 未認証・オンプレ環境など取得できない場合はundefinedを返す（例外は投げない）
 * @returns アクセストークン文字列、取得できない場合はundefined
 */
export const getAccessToken = async (): Promise<string | undefined> => {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString();
  } catch {
    return undefined;
  }
};

// ctx.customerIdによるアクセス制御が必要なナレッジ関連のprocedureパス
export const KNOWLEDGE_AUTH_PATHS = new Set([
  "dify.getKnowledgeBases",
  "dify.getKnowledgeBaseDocuments",
  "dify.addCustomKnowledge",
  "dify.addDifyKnowledgeWithFile",
  "dify.deleteKnowledgeBaseDocument",
  "dify.deleteKnowledgeBaseData",
  "dify.addDocumentByText",
  "dify.addDocumentByFile",
  "dify.getDocumentDetail",
]);

export const api = createTRPCProxyClient<AppRouter>({
  links: [
    splitLink({
      condition: (op) => KNOWLEDGE_AUTH_PATHS.has(op.path),
      // ナレッジ系: Authorizationヘッダを付与してサーバー側でcustomerIdを確定
      true: httpBatchLink({
        url: getBaseUrl(),
        headers: async () => {
          const token = await getAccessToken();
          return token ? { Authorization: `Bearer ${token}` } : {};
        },
      }),
      // その他: 従来通りヘッダなし
      false: httpBatchLink({
        url: getBaseUrl(),
      }),
    }),
  ],
});
